import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Button,
  Card,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  StatusBadge,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  ActivityDetailModal,
  TicketActivityFeed,
  buildActivitiesFromTanssHistory,
  type ActivityItem,
  type TanssHistoryRaw,
} from '@/components/tickets'
import { customerApi } from '../lib/api'
import {
  AppointmentBookingForm,
  DEFAULT_APPOINTMENT_VALUE,
  slotToAppointmentPayload,
  type AppointmentBookingValue,
} from '../components/AppointmentBookingForm'

interface TicketDetailResponse {
  ticket: Record<string, unknown>
  comments: Record<string, unknown>[]
  history?: TanssHistoryRaw
}

export function CustomerTicketDetailPage() {
  const { id } = useParams()
  const { toast } = useToast()
  const [data, setData] = useState<TicketDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [posting, setPosting] = useState(false)
  const [showCallback, setShowCallback] = useState(false)
  const [callbackPhone, setCallbackPhone] = useState('')
  const [callbackTime, setCallbackTime] = useState('')
  const [showAppointment, setShowAppointment] = useState(false)
  const [appointment, setAppointment] = useState<AppointmentBookingValue>(DEFAULT_APPOINTMENT_VALUE)
  const [selectedItem, setSelectedItem] = useState<ActivityItem | null>(null)

  const load = async () => {
    if (!id) return
    setLoading(true)
    try {
      const res = await customerApi.get<TicketDetailResponse>(`/tickets/${id}`)
      setData(res.data)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id])

  const submitComment = async () => {
    if (!id || !comment.trim()) return
    setPosting(true)
    try {
      await customerApi.post(`/tickets/${id}/comments`, { content: comment })
      setComment('')
      await load()
    } catch (e) {
      toast({
        title: 'Fehler',
        description: e instanceof Error ? e.message : 'Kommentar konnte nicht gesendet werden',
        tone: 'danger',
      })
    } finally {
      setPosting(false)
    }
  }

  const openAppointment = () => {
    setAppointment(DEFAULT_APPOINTMENT_VALUE)
    setShowAppointment(true)
  }

  const submitCallback = async () => {
    if (!id || !callbackPhone.trim()) return
    try {
      await customerApi.post(`/tickets/${id}/callback`, {
        phone: callbackPhone.trim(),
        preferredTime: callbackTime.trim() || undefined,
      })
      toast({
        title: 'Rückruf angefordert',
        description: 'Dein Ansprechpartner meldet sich zeitnah.',
        tone: 'success',
      })
      setShowCallback(false)
      setCallbackPhone(''); setCallbackTime('')
      await load()
    } catch (e) {
      toast({
        title: 'Fehler',
        description: e instanceof Error ? e.message : 'Rückruf konnte nicht gesendet werden',
        tone: 'danger',
      })
    }
  }

  const submitAppointment = async () => {
    if (!id || !appointment.slot) return
    try {
      await customerApi.post(
        `/tickets/${id}/appointment`,
        slotToAppointmentPayload(appointment.slot, appointment.location, appointment.note),
      )
      toast({
        title: 'Terminwunsch gesendet',
        description: 'Dein Ansprechpartner bestätigt den Termin in Kürze.',
        tone: 'success',
      })
      setShowAppointment(false)
      setAppointment(DEFAULT_APPOINTMENT_VALUE)
      await load()
    } catch (e) {
      toast({
        title: 'Fehler',
        description: e instanceof Error ? e.message : 'Terminwunsch konnte nicht gesendet werden',
        tone: 'danger',
      })
    }
  }

  // Backend already filters out internal entries (`excludeInternal` is
  // defense-in-depth in case the backend filtering is ever relaxed).
  const activities = useMemo<ActivityItem[]>(() => {
    if (!data) return []
    const history: TanssHistoryRaw = data.history ?? {
      comments: data.comments ?? [],
      supports: [],
      mails: [],
    }
    return buildActivitiesFromTanssHistory(history, { excludeInternal: true })
  }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState details={error} />
  if (!data) return null
  const t = data.ticket
  // TANSS returns statusId as a number — `as string` alone does not change the
  // runtime type, so we explicitly call String(). Otherwise StatusBadge crashes
  // with "e.toLowerCase is not a function".
  const statusRaw = t.status ?? t.statusId
  const status = statusRaw != null ? String(statusRaw) : undefined

  return (
    <div>
      <PageHeader
        title={(t.title as string) ?? 'Ticket'}
        description={status ? undefined : 'Ticketdetails'}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowCallback(true)}>Rückruf anfordern</Button>
            <Button variant="primary" onClick={openAppointment}>Termin vereinbaren</Button>
          </div>
        }
      />
      {status ? (
        <div className="mb-4">
          <StatusBadge status={status} />
        </div>
      ) : null}
      <Card>
        <Card.Body>
          <div className="whitespace-pre-wrap text-body text-gray-800">
            {(t.content as string) ?? (t.description as string) ?? '–'}
          </div>
        </Card.Body>
      </Card>

      <TicketActivityFeed
        className="mt-6"
        items={activities}
        onItemClick={setSelectedItem}
        emptyHint="Sobald Ihr Ansprechpartner einen Kommentar, eine Leistung, einen Termin oder eine E-Mail veröffentlicht, erscheint sie hier."
      />

      <Card className="mt-6">
        <Card.Body>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} label="Antwort" />
          <div className="flex justify-end mt-2">
            <Button variant="primary" onClick={submitComment} loading={posting} disabled={!comment.trim()}>
              Kommentar senden
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal
        open={showCallback}
        onOpenChange={(o) => { if (!o) setShowCallback(false) }}
        title="Rückruf anfordern"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCallback(false)}>Abbrechen</Button>
            <Button variant="primary" onClick={submitCallback} disabled={!callbackPhone.trim()}>
              Rückruf anfordern
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Telefonnummer" value={callbackPhone} onChange={(e) => setCallbackPhone(e.target.value)} required placeholder="+49 …" />
          <Input label="Wunschzeitraum (optional)" value={callbackTime} onChange={(e) => setCallbackTime(e.target.value)} placeholder="z. B. heute 14–16 Uhr" />
        </div>
      </Modal>

      <Modal
        open={showAppointment}
        onOpenChange={(o) => { if (!o) setShowAppointment(false) }}
        title="Termin vereinbaren"
        size="xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowAppointment(false)}>Abbrechen</Button>
            <Button variant="primary" onClick={submitAppointment} disabled={!appointment.slot}>
              {appointment.location === 'onsite' ? 'Termin anfragen' : 'Termin buchen'}
            </Button>
          </>
        }
      >
        <AppointmentBookingForm
          ticketId={id ?? null}
          value={appointment}
          onChange={setAppointment}
        />
      </Modal>

      <ActivityDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
    </div>
  )
}
